/**
 * Staff portal session continuity (Business Rule #41).
 *
 * The staff portal is deliberately unlinked from customer navigation, so when
 * Android reaps the app mid-shift the cold start lands on the CUSTOMER home
 * screen with no way back except Profile -> Staff badge. That is what "the app
 * crashed and threw me out of staff login" was at Nahar on 2026-08-20 — the
 * session was never lost, only the route.
 *
 * StaffLayout records the current staff path on every navigation and CLEARS it
 * on unmount. That distinction is the whole trick: leaving the portal on
 * purpose runs the cleanup, being killed does not — so a stored path still
 * sitting there on a cold start means the app died with staff mid-task, and
 * only then do we restore.
 */
const KEY = 'pb_staff_last_route';

/** How long a stored route stays restorable. Past this, a cold start is a
 *  fresh open, not a crash recovery. */
const MAX_AGE_MS = 30 * 60 * 1000;

/** Record where staff currently are. Ignores non-staff paths. */
export function rememberStaffRoute(path) {
  try {
    if (!path || !path.startsWith('/staff')) return;
    localStorage.setItem(KEY, JSON.stringify({ path, at: Date.now() }));
  } catch { /* private mode / quota — continuity is best-effort */ }
}

/** Forget the stored route. Called when staff LEAVE the portal deliberately. */
export function forgetStaffRoute() {
  try { localStorage.removeItem(KEY); } catch { /* no-op */ }
}

/**
 * The staff path to restore on a cold start, or null. Reading CONSUMES it —
 * a restore is offered once, never on every subsequent launch.
 */
export function takeStaffRoute() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    localStorage.removeItem(KEY);
    const { path, at } = JSON.parse(raw) || {};
    if (!path || typeof path !== 'string' || !path.startsWith('/staff')) return null;
    if (!at || Date.now() - at > MAX_AGE_MS) return null;
    return path;
  } catch {
    return null;
  }
}
